/**
 * SpeedmasterWarranty.PDP.View.js
 * Renders the warranty opt-in checkbox card on the Product Detail Page.
 *
 * Mounted as a child view of ProductDetails.Full.View via PDP component's
 * addChildViews API (see Shopping.js).
 *
 * Single checkbox card: click the card to opt in/out.
 * Stores choice in sessionStorage AND toggles the native hidden
 * custcol_warranty_opted checkbox so SuiteCommerce includes the
 * option natively at Add to Cart time.
 */
define('SpeedmasterWarranty.PDP.View', [
    'speedmaster_warranty_pdp.tpl',
    'SpeedmasterWarranty.Config',
    'Backbone',
    'jQuery'
], function SpeedmasterWarrantyPDPViewDef(
    speedmaster_warranty_pdp_tpl,
    WarrantyConfig,
    Backbone,
    jQuery
) {
    'use strict';

    return Backbone.View.extend({

        template: speedmaster_warranty_pdp_tpl,

        events: {
            'click [data-action="warranty-toggle"]':   'onToggle',
            'keydown [data-action="warranty-toggle"]': 'onToggleKeydown'
        },

        initialize: function initialize(options) {
            var self = this;
            self.productModel = (options && options.model) ? options.model : null;

            // PDP component: try options first, fall back to Config singleton
            self.pdpComponent = (options && options.pdpComponent)
                ? options.pdpComponent
                : WarrantyConfig.getPdpComponent();

            self.cfg = WarrantyConfig.get();
        },

        /**
         * Get the item info as a flat object with custom fields accessible
         * directly (e.g. itemInfo.custitem_extendedwarranty).
         *
         * Uses PDP component Extensibility API as primary source —
         * pdp.getItemInfo() returns { item: {...}, quantity: N, ... }
         * so we unwrap .item to normalise the structure.
         * @returns {Object|null}
         */
        _getItemInfo: function _getItemInfo() {
            var self = this;

            // Primary: PDP component Extensibility API
            if (self.pdpComponent && typeof self.pdpComponent.getItemInfo === 'function') {
                try {
                    var pdpInfo = self.pdpComponent.getItemInfo();
                    if (pdpInfo) {
                        return pdpInfo.item || pdpInfo;
                    }
                } catch (e) {}
            }

            // Fallback: from Backbone model passed by parent view
            if (self.productModel && typeof self.productModel.get === 'function') {
                var item = self.productModel.get('item');
                if (item) {
                    return (typeof item.toJSON === 'function') ? item.toJSON() : item;
                }
            }

            return null;
        },

        _isEligible: function _isEligible() {
            var self = this;
            var itemInfo = self._getItemInfo();
            if (!itemInfo) { return false; }

            // Not purchasable (out of stock) = no warranty option
            if (itemInfo.ispurchasable === false || itemInfo.ispurchasable === 'F') {
                return false;
            }

            var cfg = self.cfg;
            var fieldValue = null;

            if (itemInfo.extras) {
                fieldValue = itemInfo.extras[cfg.eligibilityField];
            }
            if (fieldValue == null) {
                fieldValue = itemInfo[cfg.eligibilityField];
            }

            return fieldValue === true || fieldValue === 'T' || fieldValue === 'true' ||
                   (!!fieldValue && fieldValue !== false && fieldValue !== 'F' && fieldValue !== 'false' && fieldValue !== 'N');
        },

        _getItemId: function _getItemId() {
            var itemInfo = this._getItemInfo();
            if (!itemInfo) { return ''; }
            return (itemInfo.internalid || '').toString();
        },

        _getItemPrice: function _getItemPrice() {
            var itemInfo = this._getItemInfo();
            if (!itemInfo) { return 0; }
            var priceDetail = itemInfo.onlinecustomerprice_detail;
            if (priceDetail && priceDetail.onlinecustomerprice) {
                return Number(priceDetail.onlinecustomerprice) || 0;
            }
            return 0;
        },

        /* ── template context ────────────────────────────────────── */
        getContext: function getContext() {
            var self = this;
            var cfg = self.cfg;
            var eligible = self._isEligible();
            var itemPrice = self._getItemPrice();
            var warrantyCost = eligible ? WarrantyConfig.calcLineWarrantyCost(itemPrice) : 0;
            var itemId = self._getItemId();
            var warrantyIsActive = itemId ? WarrantyConfig.isItemOpted(itemId) : false;

            // Term badge: "3 Years" / "1 Year"
            var years = cfg.termYears || 3;
            var termBadge = years + ' Year' + (years > 1 ? 's' : '');

            // Description: "3-year extended coverage beyond the 12-month manufacturer warranty"
            var mfgMonths = cfg.manufacturerWarrantyMonths || 12;
            var warrantyDescription = years + '-year extended coverage beyond the manufacturer warranty';

            return {
                isEligible:             eligible,
                warrantyIsActive:       warrantyIsActive,
                termLabel:              cfg.termLabel,
                termBadge:              termBadge,
                warrantyCostFormatted:  WarrantyConfig.formatPrice(warrantyCost),
                warrantyCost:           warrantyCost,
                warrantyDescription:    warrantyDescription,
                showOption:             eligible && warrantyCost > 0,
                itemId:                 itemId
            };
        },

        /* ── event handlers ──────────────────────────────────────── */

        onToggle: function onToggle(e) {
            // Let info link clicks navigate — don't toggle
            if (jQuery(e.target).closest('a').length) { return; }
            e.preventDefault();
            var itemId = this._getItemId();
            var currentState = itemId ? WarrantyConfig.isItemOpted(itemId) : false;
            var newState = !currentState;

            if (itemId) {
                WarrantyConfig.setItemOpted(itemId, newState);
            }
            this._toggleNativeCheckbox(newState);
            this.render();
        },

        onToggleKeydown: function onToggleKeydown(e) {
            // Enter or Space activates the checkbox
            if (e.which === 13 || e.which === 32) {
                e.preventDefault();
                this.onToggle(e);
            }
        },

        /**
         * Toggle the native hidden custcol_warranty_opted checkbox that
         * SuiteCommerce auto-renders on the PDP for registered item options.
         *
         * We hide it with CSS but it still exists in the DOM. Toggling it
         * programmatically triggers SuiteCommerce's built-in event handler,
         * which updates the product model's options. When Add to Cart fires,
         * the option persists natively to the Sales Order — no custom
         * service, updateLine, or UE script needed.
         *
         * @param {boolean} checked  Whether to check (true) or uncheck (false)
         */
        _toggleNativeCheckbox: function _toggleNativeCheckbox(checked) {
            var $checkbox = jQuery('[data-cart-option-id="custcol_warranty_opted"] input[type="checkbox"]');
            if ($checkbox.length) {
                $checkbox.prop('checked', checked).trigger('change');
            }
        }

    });
});
